import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast, Toaster } from "sonner";
import { Eye, LogOut, Pencil, Plus, Star, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { adminApi, fetchCategories } from "@/lib/blogApi";
import ArticleEditor from "@/components/blog/ArticleEditor";
import EditorsPanel from "@/components/blog/EditorsPanel";
import { formatDate } from "@/components/blog/shared";
import "@/blog.css";

const LoginForm = ({ onError }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) onError(error.message);
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <p className="font-heading text-xs tracking-[0.4em] uppercase text-[#A855F7] mb-4 text-center">Voveron CMS</p>
        <h1 className="font-heading text-2xl font-bold text-white text-center mb-10">Sign in to the blog dashboard</h1>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <div>
            <label className="admin-label">Email</label>
            <input type="email" className="admin-input" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-login-email" />
          </div>
          <div>
            <label className="admin-label">Password</label>
            <input type="password" className="admin-input" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-login-password" />
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full py-3 font-semibold" data-testid="admin-login-submit">
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
        <Link to="/blog" className="block text-center text-sm text-[#A1A1AA] hover:text-white mt-8">← Back to the blog</Link>
      </div>
    </div>
  );
};

const StatusPill = ({ status }) => (
  <span className={`status-pill status-${status}`} data-testid={`status-pill-${status}`}>{status}</span>
);

export default function AdminBlog() {
  const [session, setSession] = useState(null);
  const [staff, setStaff] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [view, setView] = useState("list"); // list | edit | editors
  const [editingId, setEditingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  const token = session?.access_token;

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setAuthChecked(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  const loadArticles = useCallback(() => {
    if (!token) return;
    adminApi.listArticles(token).then(setArticles).catch(() => toast.error("Failed to load articles"));
  }, [token]);

  const closeEditor = useCallback(() => {
    setView("list");
    setEditingId(null);
  }, []);

  useEffect(() => {
    if (!token) {
      setStaff(null);
      setAccessDenied(false);
      return;
    }
    adminApi
      .me(token)
      .then((me) => {
        setStaff(me);
        setAccessDenied(false);
      })
      .catch(() => setAccessDenied(true));
    fetchCategories().then(setCategories).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (staff) loadArticles();
  }, [staff, loadArticles]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setStaff(null);
    setView("list");
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete "${a.title}" permanently?`)) return;
    try {
      await adminApi.deleteArticle(token, a.id);
      toast.success("Article deleted");
      loadArticles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const togglePublish = async (a) => {
    try {
      const full = await adminApi.getArticle(token, a.id);
      const newStatus = a.status === "published" ? "draft" : "published";
      await adminApi.updateArticle(token, a.id, { ...full, status: newStatus });
      toast.success(newStatus === "published" ? "Article published" : "Article unpublished");
      loadArticles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  if (!authChecked) return <div className="min-h-screen bg-[#09090b]" />;
  if (!session) return (
    <>
      <Toaster theme="dark" position="top-right" />
      <LoginForm onError={(m) => toast.error(m)} />
    </>
  );

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-6">
        <div className="text-center" data-testid="admin-access-denied">
          <h1 className="font-heading text-2xl font-bold text-white mb-4">No blog access</h1>
          <p className="text-[#A1A1AA] mb-8">This account isn't registered as an admin or editor.</p>
          <button onClick={signOut} className="btn-primary px-6 py-2.5 font-semibold" data-testid="admin-denied-signout">Sign Out</button>
        </div>
      </div>
    );
  }

  if (!staff) return <div className="min-h-screen bg-[#09090b]" />;

  const filtered = statusFilter ? articles.filter((a) => a.status === statusFilter) : articles;
  const isAdmin = staff.role === "admin";

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="admin-dashboard">
      <Helmet>
        <title>Blog CMS | VOVERON</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Toaster theme="dark" position="top-right" />

      <header className="border-b border-[#27272A] glass-panel sticky top-0 z-40">
        <div className="container-main py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="Voveron logo" className="h-7 w-auto" />
              <span className="font-heading text-xl font-bold text-white">VOVERON</span>
            </Link>
            <span className="text-xs uppercase tracking-widest text-[#A855F7] font-heading">Blog CMS</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs text-[#A1A1AA]" data-testid="admin-user-info">
              {staff.email} · <span className="text-[#A855F7] uppercase">{staff.role}</span>
            </span>
            <button onClick={signOut} className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="admin-signout" title="Sign out">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="container-main py-10">
        {view === "edit" ? (
          <ArticleEditor
            token={token}
            articleId={editingId}
            categories={categories}
            onBack={closeEditor}
            onSaved={loadArticles}
          />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div className="flex gap-2" data-testid="admin-tabs">
                <button className={`cat-pill ${view === "list" ? "active" : ""}`} onClick={() => setView("list")} data-testid="admin-tab-articles">
                  Articles
                </button>
                {isAdmin && (
                  <button className={`cat-pill ${view === "editors" ? "active" : ""}`} onClick={() => setView("editors")} data-testid="admin-tab-editors">
                    Team
                  </button>
                )}
              </div>
              {view === "list" && (
                <button
                  className="btn-primary px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2"
                  onClick={() => { setEditingId(null); setView("edit"); }}
                  data-testid="new-article-button"
                >
                  <Plus size={16} /> New Article
                </button>
              )}
            </div>

            {view === "editors" ? (
              <EditorsPanel token={token} currentUserId={staff.user_id} />
            ) : (
              <>
                <div className="flex gap-2 mb-6" data-testid="status-filters">
                  {["", "published", "scheduled", "draft"].map((s) => (
                    <button
                      key={s || "all"}
                      className={`cat-pill ${statusFilter === s ? "active" : ""}`}
                      onClick={() => setStatusFilter(s)}
                      data-testid={`status-filter-${s || "all"}`}
                    >
                      {s || "All"}
                    </button>
                  ))}
                </div>

                <div className="border border-[#27272A] divide-y divide-[#27272A]" data-testid="admin-articles-list">
                  {filtered.length === 0 && (
                    <p className="p-10 text-center text-[#A1A1AA]" data-testid="admin-articles-empty">No articles yet. Create your first one.</p>
                  )}
                  {filtered.map((a) => (
                    <div key={a.id} className="p-5 flex flex-col md:flex-row md:items-center gap-4 bg-[#0A0A0B] hover:bg-[#0f0f11] transition-colors" data-testid={`admin-article-row-${a.slug}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1.5">
                          {a.is_featured && <Star size={14} className="text-[#A855F7] shrink-0" fill="currentColor" />}
                          <h3 className="font-heading font-semibold text-white truncate">{a.title}</h3>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-[#A1A1AA]">
                          <StatusPill status={a.status} />
                          {a.category_name && <span>{a.category_name}</span>}
                          <span>Updated {formatDate(a.updated_at)}</span>
                          {a.published_at && <span>Publish date: {formatDate(a.published_at)}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          className="cat-pill"
                          onClick={() => togglePublish(a)}
                          data-testid={`toggle-publish-${a.slug}`}
                        >
                          {a.status === "published" ? "Unpublish" : "Publish"}
                        </button>
                        {a.status === "published" && (
                          <a href={`/blog/${a.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-[#A1A1AA] hover:text-[#A855F7]" title="View" data-testid={`view-article-${a.slug}`}>
                            <Eye size={16} />
                          </a>
                        )}
                        <button
                          className="p-2 text-[#A1A1AA] hover:text-[#A855F7]"
                          onClick={() => { setEditingId(a.id); setView("edit"); }}
                          title="Edit"
                          data-testid={`edit-article-${a.slug}`}
                        >
                          <Pencil size={16} />
                        </button>
                        {isAdmin && (
                          <button className="p-2 text-[#A1A1AA] hover:text-red-400" onClick={() => handleDelete(a)} title="Delete" data-testid={`delete-article-${a.slug}`}>
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
